import {AssetSupplier, PpsrAsset} from '../domain/ppsr-service.domain';
import {
  Application,
  BusinessOverdraftApplication,
  CONSENT_COMPLETED_LABEL,
  CONSENT_DECLINED_LABEL,
  CONSENT_DISPATCH_FAILED_SHORT_LABEL,
  CONSENT_EXPIRED_LABEL,
  CONSENT_EXPIRED_SIGNATURE_LABEL,
  CONSENT_EXPIRED_UNSIGNED_LABEL,
  CONSENT_INVALIDATED_LABEL,
  CONSENT_MANUALLY_COMPLETED_LABEL,
  CONSENT_PENDING_SIGNATURE_LABEL,
  CONSENT_SUPERSEDED_LABEL,
  ConsentFormPrefillData,
  PrivacyConsent,
  SimplifiedApplication, WizardSubmitData
} from '../domain/application-service.domain';
import type {
  ApplicationCommercialEntity,
  ApplicationIndividual,
  GetApplicationPartiesPayload,
  JsonObject,
} from '../domain/application-parties.domain';
import {commercialEntityRoleType} from './application-party-labels';
import {Address2ComponentValue} from '../domain/component/address2-component.domain';
import {parseJSON, stringifyJSON} from '../utils/json';
import {
  constants,
  CONSENT_FORM_COMPANY_BLOCK_FIELDS,
  CONSENT_FORM_CRITICAL_FIELDS,
  CONSENT_FORM_EQUIFAX_FIELDS,
  CONSENT_MAX_AGE_DAYS,
  getAssetCategory as getAssetCategoryFromConst,
  getAssetCategoryType as getAssetCategoryTypeFromConst,
} from '../const';

import moment from 'moment';
import {PaymentPeriodType} from "../domain/types.domain";
import { VerimotoInspectionTypeSelection } from '../domain/verimoto.domain';
import { TermRateForBusinessOverdraft, thresholdMeets } from '../app-calculator';
import { RatecardDetails } from '../domain/application-service.domain';
import * as _ from "lodash";

const STAGE_NAMES_UNDER_REVIEW = ['submitted to credit', 'waiting for bank statements', 'more information'];
const STAGE_NAMES_IN_SETTLEMENT = ['docs out', 'settlement pending', 'qa', 'settlement'];
const STAGE_NAMES_CLOSED_WON = ['closed won'];
const STAGE_NAMES_APPROVED = ['Credit Approved', 'Documentation'];

export const isApplicationInSettlement = (app: Pick<SimplifiedApplication, 'StageName'>): boolean => {
  return STAGE_NAMES_IN_SETTLEMENT.includes(_.toLower(_.trim(app.StageName ?? '')));
}
export const isApplicationUnderReview = (app: Pick<SimplifiedApplication, 'StageName'>): boolean => {
  return STAGE_NAMES_UNDER_REVIEW.includes(_.toLower(_.trim(app.StageName ?? '')));
}
export const isApplicationClosedWon = (app: Pick<SimplifiedApplication, 'StageName'>): boolean => {
  return STAGE_NAMES_CLOSED_WON.includes(_.toLower(_.trim(app.StageName ?? '')));
}

export const isApplicationApproved = (app: Pick<SimplifiedApplication, 'StageName'>): boolean => {
  return STAGE_NAMES_APPROVED.includes(_.toLower(_.trim(app.StageName ?? '')));
}

/**
 * Builds the record the wizards cache locally so a part-finished application shows up in the draft list.
 *
 * Reads the first entity and individual positionally rather than by role, because this runs on
 * in-progress wizard data where no role or primary flag has been assigned yet.
 *
 * Typed `WizardSubmitData`, not `UpdateApplicationData`, so the legacy party arrays are not merely
 * unused here but unreadable — reading them silently produced nulls once the wizards stopped sending
 * them, and the optional keys made that assignable rather than a compile error.
 */
export const toDraftSimplifiedApplication = (applicationId: number, app: WizardSubmitData): SimplifiedApplication => {
  const firstEntity = app?.parties?.commercialEntities?.[0];
  const firstIndividual = app?.parties?.individuals?.[0];
  const s: SimplifiedApplication = {
    ApplicationId: applicationId,
    Source: app.Source ?? 'portal',
    BrokerAppId: app?.AppInfo?.BrokerAppID ?? '',
    ApplicationType: app.ApplicationType,
    Status: app?.Status ?? 'Draft',
    CreateTime: moment().toISOString(),
    CommercialEntityLegalName: firstEntity?.legalName ?? null,
    IndividualGivenName: firstIndividual?.givenName ?? null,
    IndividualSurName: firstIndividual?.surname ?? null,
    SalesforceId: app?.AppInfo?.SalesforceId ?? null,
    BrokerSalesforceID: app?.AppInfo?.BrokerSalesforceID ?? null,
    StageName: app?.AppInfo?.StageName ?? '',
    CompanyName: firstEntity?.legalName ?? '',
    BrokerName: `${app?.BrokerContact?.FirstName ?? ''} ${app?.BrokerContact?.LastName ?? ''}`.trim(),
    PricingDetails: {
      LoanAmount: app.PricingDetails?.LoanAmount ?? 0,
    },
    opportunityOwnerEmail: app?.BrokerContact?.Email ?? null,
    creditOfficerEmail: app?.BrokerContact?.Email ?? null
  };
  return s;
}

export const isPrimaryApplicantWithABN = (application: Application): boolean => {
  const primary = primaryEntityOf(application.parties);
  if (primary) {
    const isTrust = primary?.entityType === 'TRST';
    const isSLTR = primary?.entityType === 'SLTR';
    const isPTNR = primary?.entityType === 'PTNR';
    return (isPTNR || isSLTR || isTrust)
  }
  return false
}

export const isCompanyOrEntityOrg = (application: Application): boolean => {
  const primary = primaryEntityOf(application.parties);
  if (primary) {
    const isPty = primary.entityType === 'PTY';
    const entities = application.parties?.commercialEntities ?? [];
    const multiCommercialEntity = entities.some(entity => commercialEntityRoleType(entity) === 'Trustee');
    return (isPty || multiCommercialEntity);
  }
  return false;
}

export const getLoanPurpose = (application: Application): string | null => {
  if (application) {
    return application.AppInfo.LoanPurpose?.name ?? null;
  }
  return null;
}

export const getProductType = (application: Application): string | null => {
  if (application) {
    switch (application.ApplicationType) {
      case 'AssetFinance':
        return application.AppInfo.productType;
    }
  }
  return null;
}

export const getSwitchToEasyDoc = (application: Application): boolean | null => {
  if (application) {
    switch (application.ApplicationType) {
      case 'AssetFinance':
        return application.AppInfo.isSwitchToEasyDoc;
    }
  }
  return null;
}

export const getCompanyName = (application: Application) => {
  const commercialEntity =  primaryEntityOf(application.parties);
  return commercialEntity?.legalName ?? '';
}

const trustNameForDisplay = (legalName: string): string =>
  legalName
    .trim()
    .toUpperCase()
    .replace(/^.*\b(?:IIOR|IHOR|ITOR)\s+&\s+ATF\s+/, '')
    .replace(/^THE TRUSTEE FOR\s+/, '');

const individualTrusteeName = (individual: ApplicationIndividual): string =>
  [individual.givenName, individual.middleName, individual.surname]
    .filter(part => !!part?.trim())
    .join(' ')
    .trim()
    .toUpperCase();

/**
 * Name shown for the primary borrower on the application header.
 * A trust is written in trustee capacity (AZ-1642): one entity trustee uses IIOR,
 * one individual trustee uses IHOR, and several individual trustees use ITOR.
 * Anything that is not an unambiguous trust of those shapes keeps its legal name.
 */
export const applicationEntityDisplayName = (application: Application): string => {
  const primary = primaryEntityOf(application.parties);
  const legalName = primary?.legalName?.trim() ?? '';
  if (!primary || primary.entityType !== 'TRST') return legalName;

  const trustName = trustNameForDisplay(legalName);
  if (!trustName) return legalName;

  const entities = application.parties?.commercialEntities ?? [];
  const individuals = application.parties?.individuals ?? [];
  const corporateTrustees = entities.filter(entity =>
    entity.id !== primary.id &&
    !entity.isDeleted &&
    !!entity.legalName?.trim() &&
    entity.relationships.some(relationship => relationship.isTrustee && relationship.ownerEntityId === primary.id),
  );
  const individualTrustees = individuals.filter(individual =>
    !individual.isDeleted &&
    individual.roles.some(role => role.isTrustee && role.entityId === primary.id),
  );

  if (corporateTrustees.length === 1 && individualTrustees.length === 0) {
    return `${corporateTrustees[0].legalName!.trim().toUpperCase()} IIOR & ATF ${trustName}`;
  }

  if (corporateTrustees.length === 0 && individualTrustees.length > 0) {
    const names = individualTrustees.map(individualTrusteeName).filter(Boolean);
    if (names.length) {
      const capacity = names.length === 1 ? 'IHOR' : 'ITOR';
      return `${names.join(' & ')} ${capacity} & ATF ${trustName}`;
    }
  }

  return legalName;
}

const formatAgeMonths = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '';
  }
  return String(Math.round(value));
};

export const getAbnAge = (application: Application): string => {
  const synced = application?.GrowRiskRatingData;
  if (synced) {
    return formatAgeMonths(synced.abnAge);
  }
  return formatAgeMonths(application?.CompanyDetails?.ABNAgeMonths);
};

export const getAbnAgeWithContinuity = (application: Application): string => {
  const synced = application?.GrowRiskRatingData;
  if (synced) {
    return formatAgeMonths(synced.abnAgeWithContinuity ?? synced.abnAge);
  }
  const company = application?.CompanyDetails;
  return formatAgeMonths(company?.ABNAgeMonthsWithContinuity ?? company?.ABNAgeMonths);
};

export const getGstAge = (application: Application): string => {
  const synced = application?.GrowRiskRatingData;
  if (synced) {
    return formatAgeMonths(synced.gstAge);
  }
  return formatAgeMonths(application?.CompanyDetails?.GSTAgeMonths);
};

export const getGstAgeWithContinuity = (application: Application): string => {
  const synced = application?.GrowRiskRatingData;
  if (synced) {
    return formatAgeMonths(synced.gstAgeWithContinuity ?? synced.gstAge);
  }
  const company = application?.CompanyDetails;
  return formatAgeMonths(company?.GSTAgeMonthsWithContinuity ?? company?.GSTAgeMonths);
};

export const getBrokerContact = (application: Application) => {
  const brokerContact = application.BrokerContact
  if(brokerContact) {
    return   brokerContact?.FirstName +' '+  brokerContact?.LastName
  }
  return '';
}

export const getSubmitterContact = (application: Application) => {
  const submitterContact = application.AppInfo.UserFirstName +' '+  application.AppInfo.UserLastName
  if(submitterContact) {
    return   submitterContact
  }
  return '';
}

export const getPreviousBusinessName = (application: Application) => {
  if (application && application.ApplicationType !== 'InsurancePremium' && application.ApplicationType !== 'Consumer') {
    return application.PreviousBusiness?.name ?? '';
  }
  return '';
}

export const getPreviousBusinessABN = (application: Application) => {
  if (application && application.ApplicationType !== 'InsurancePremium' && application.ApplicationType !== 'Consumer') {
    return application.PreviousBusiness?.abn ?? '';
  }
  return '';
}

export const getPreviousBusinessACN = (application: Application) => {
  if (application && application.ApplicationType !== 'InsurancePremium' && application.ApplicationType !== 'Consumer') {
    return application.PreviousBusiness?.acn ?? '';
  }
  return '';
}

export const getPreviousBusiness = (application: Application) => {
  if (application && application.ApplicationType !== 'InsurancePremium' && application.ApplicationType !== 'Consumer') {
    return application.PreviousBusiness;
  }
  return null;
}

export const getApplicationName = (application: Application) => {
  const individual = applicantOf(application.parties);
  const givenName = individual?.givenName;
  const surName = individual?.surname;
  if (givenName || surName) {
    return `${givenName??''} ${surName??''}`.trim();
  }
  return '';
}

/**
 * The applicant: the entity-less individual borrower on a consumer application.
 *
 * Takes the parties payload rather than the application so it serves both sources of it: the
 * `parties` field the hydrator attaches, and the response from POST /api2/get-application-parties.
 */
export const applicantOf = (
  parties: GetApplicationPartiesPayload | null | undefined,
): ApplicationIndividual | null =>
  (parties?.individuals ?? []).find(individual => individual.isApplicant) ?? null;

/**
 * The borrowing entity, identified by `isPrimary` rather than its position.
 *
 * Deliberately without `primaryEntityRow`'s first-entity fallback. That fallback exists for callers
 * that need *an* entity; these prefill readers must return null when no entity is flagged, or a
 * wizard would prefill from a guarantor company.
 */
export const primaryEntityOf = (
  parties: GetApplicationPartiesPayload | null | undefined,
): ApplicationCommercialEntity | null =>
  (parties?.commercialEntities ?? []).find(entity => entity.isPrimary) ?? null;

/**
 * The spouse in the redesigned shape, for the consumer prefill readers.
 *
 * Deliberately a superset of the legacy `Role === 'Spouse'` lookup it replaces. Spouse is the
 * second-lowest precedence branch of `apiRoleFromRoleFlags`, above only Guarantor, so that string
 * appeared only for someone holding no other role — anyone who was both a spouse and, say, a director
 * read as 'Director' and the old lookup skipped them. Reading the flag finds them.
 *
 * The two sets can therefore only differ for a spouse who also holds a role against an entity, and
 * every caller is consumer-only, where there is no entity for such a role to attach to. AZ-1475.
 */
export const spouseOf = (
  parties: GetApplicationPartiesPayload | null | undefined,
): ApplicationIndividual | null =>
  (parties?.individuals ?? []).find(individual => individual.roles.some(role => role.isSpouse)) ?? null;

/**
 * The name to show for whoever is applying: the borrowing entity on a commercial application, the
 * applicant individual on a consumer one.
 *
 * Empty string when neither resolves, matching what the notification call sites already did with a
 * missing value. Consumer applications are the only ones with an `Applicant`-role individual, which is
 * why the two branches ask different questions.
 */
export const applicantDisplayName = (application: Application | null | undefined): string => {
  if (!application) {
    return '';
  }
  if (application.ApplicationType !== 'Consumer') {
    return primaryEntityOf(application.parties)?.legalName ?? '';
  }
  const applicant = applicantOf(application.parties);
  return `${applicant?.givenName ?? ''} ${applicant?.surname ?? ''}`.trim();
};

/**
 * The individual nominated as the application's primary contact. Applies to every product, unlike
 * `applicantOf`.
 *
 * Not guaranteed to resolve — older applications and some KYB-added parties have none — so callers
 * needing someone to contact regardless should fall back explicitly. No soft-delete filter: the
 * projection that builds this payload already excludes deleted individuals.
 */
export const primaryContactOf = (
  parties: GetApplicationPartiesPayload | null | undefined,
): ApplicationIndividual | null =>
  (parties?.individuals ?? []).find(individual => individual.isPrimaryContact) ?? null;

export const getApplicantDob = (application: Application): string | null => {
  const individual = applicantOf(application.parties);
  if (individual) {
    // Both projections format the date the same way and differ only in spelling a missing one: the
    // legacy field gave "" where this gives null. Unobservable - the one caller truthy-checks.
    return individual.dateOfBirth ?? null;  // YYYY-MM-DD
  }
  return null;
}

export const getApplicationCustomerName = (application: Application) => {
  if (application.ApplicationType === 'Consumer') {
     return getApplicationName(application);
  } else {
    return getCompanyName(application);
  }
}

export const getOrganisationType = (application: Application) => {
  const commercialEntity = primaryEntityOf(application.parties);
  const entityType =  commercialEntity?.entityType;
  switch(entityType) {
    case 'TRST':
      return 'Trust';
    case 'SLTR':
      return 'Sole Trader';
    case 'PTNR':
      return 'Partner';
    case 'P/L':
      return 'Company';
    case 'PTY':
      return 'Company';
    default:
      return '';
  }
}

export const getOrganisationTypeForTitle = (application: Application) => {
  const commercialEntity = primaryEntityOf(application.parties);
  const entityType =  commercialEntity?.entityType;
  switch(entityType) {
    case 'TRST':
      return 'TRUSTEE';
    case 'SLTR':
      return 'SOLE TRADERS';
    case 'PTNR':
      return 'PARTNERS';
    case 'P/L':
      return 'COMPANY';
    case 'PTY':
      return 'DIRECTORS';
    default:
      return '';
  }
}

export const getBrokerApplicationId = (application: Application) => {
  return application.BrokerAppId;
}

export const getBrokerFlowId = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
      return application?.AppInfo?.BrokerflowDocumentID;
  }
  return '';
}

export const getBrokerSalesforceId = (application: Application) => {
  return application.BrokerSalesforceId;
}

export const getAppSalesforceId =(application: Application) => {
  return application.AppInfoSalesforceID;
}

/**
 * Submitted in the portal, whether or not Salesforce has created its records yet: the portal acts
 * on the application, and its Salesforce writes are queued behind the submission.
 */
export const isApplicationSubmitted = (application: { Status?: string | null }): boolean =>
  (application.Status ?? 'Draft') !== 'Draft';

export const getBrokerName = (application: Application) => {
  return application.BrokerName;
}

export const getAppInfoBrokerEntityName = (application: Application) => {
  return application.AppInfo.BrokerEntityName;
}

export const getOpportunityOwnerEmail = (application: Application) => {
  return application.opportunityOwnerEmail ?? '';
}

export const getOpportunityOwnerFirstName = (application: Application) => {
  return application.opportunityOwnerFirstName ?? '';
}

export const getOpportunityOwnerLastName = (application: Application) => {
  return application.opportunityOwnerLastName ?? '';
}

export const getInterestRate = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
    case 'Consumer':
      return application.PricingDetails.Rate;
  }
  return '';
}

export const getTotalAmount = (application: Application) => {
  switch(application.ApplicationType) {
    case 'InsurancePremium':
      return application.PricingDetails.TotalAmount;
  }
  return '';
}

export const getDocFee = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
    case 'Consumer':
    case 'Commercial':
    case 'InsurancePremium':
      return application.PricingDetails.DocFee;
  }
  return '';
}

export const getRepayment = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
    case 'Commercial':
    case 'InsurancePremium':
      return application.PricingDetails.Repayment;
  }
  return '';
}

export const getPaymentPeriod = (application: Application): PaymentPeriodType | '' => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
    case 'Commercial':
    case 'InsurancePremium':
      return application.PricingDetails.PaymentPeriod ?? '';
  }
  return '';
}

export const getAmountFinanced = (application: Application) => {
  // todo: ?????
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
      return application.PricingDetails.AmountFinance;
  }
  return '';
}

export const getAbn = (application: Application) => {
  const commercialEntity = primaryEntityOf(application.parties);
  if (commercialEntity?.abn) {
    return commercialEntity.abn;
  }
  // Fallback: some records have no entity flagged primary but still have ABN on the first entity.
  // Without an ABN the active-overdraft lookup never runs, and Limit Increase brokerage
  // incorrectly stays on the full facility limit (no currentLimit delta).
  const firstEntityAbn = application?.parties?.commercialEntities?.[0]?.abn;
  if (firstEntityAbn) {
    return firstEntityAbn;
  }
  const companyAbn = application?.CompanyDetails?.ABN;
  if (companyAbn) {
    const identifier = Array.isArray(companyAbn)
      ? companyAbn[0]?.identifierValue
      : companyAbn.identifierValue;
    if (identifier != null) {
      return String(identifier);
    }
  }
  return '';
}

export const getAcn = (application: Application) => {
  const commercialEntity = primaryEntityOf(application.parties);
  return commercialEntity?.acn ?? '';
}

/**
 * The borrowing corporate trustee of a trust borrower. A trust has no ACN of its own, so legal
 * documents name and identify the trust through this entity.
 */
export const getCorporateTrusteeOf = (application: Application): ApplicationCommercialEntity | null => {
  if (primaryEntityOf(application.parties)?.entityType !== 'TRST') return null;
  return (application.parties?.commercialEntities ?? [])
    .find(entity => entity.isBorrower && commercialEntityRoleType(entity) === 'Trustee') ?? null;
}

export const getFinanceType = (application: Application) => {
  return application?.AppInfo?.FinanceType;
}

export const getApplicationAssetCategory = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer': {
      const catIndex = application.AppInfo.AssetCategory;
      const cat = getAssetCategoryFromConst(catIndex);
      return cat ? cat.value : '';
    }
  }
  return '';
}

export const getAssetTypePPSR = (application: PpsrAsset) => {
  const catIndex = application.SettlementAssetDetails?.assetCategory;
  if (catIndex) {
    const catTypeIndex = application.SettlementAssetDetails?.assetType;
    if (catTypeIndex) {
      const catType = getAssetCategoryTypeFromConst(catIndex, catTypeIndex);
      return catType ? catType.value : '';
    }
  }
  return '';
}

export const getAssetCategoryObj = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer': {
      const catIndex = application.AppInfo.AssetCategory;
      const cat = getAssetCategoryFromConst(catIndex);
      return cat;
    }
  }
  return null;
}

export const getAssetCategoryIndex = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer': {
      const catIndex = application.AppInfo.AssetCategory;
      return catIndex;
    }
  }
  return '';
}

export const getAssetType = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer': {
      const catIndex = application.AppInfo.AssetCategory ?? null;
      if (catIndex) {
        const catTypeIndex = application.AppInfo.AssetType;
        if (catTypeIndex) {
          const catType = getAssetCategoryTypeFromConst(catIndex, catTypeIndex);
          return catType ? catType.value : '';
        }
      }
    }
  }
  return '';
}

export const getAssetTypeObj = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer': {
      const catIndex = application.AppInfo.AssetCategory ?? null;
      if (catIndex) {
        const catTypeIndex = application.AppInfo.AssetType;
        if (catTypeIndex) {
          const catType = getAssetCategoryTypeFromConst(catIndex, catTypeIndex);
          return catType;
        }
      }
    }
  }
  return null;
}

export const getAssetTypeIndex = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer': {
      const catIndex = application.AppInfo.AssetCategory ?? null;
      if (catIndex) {
        const catTypeIndex = application.AppInfo.AssetType;
        if (catTypeIndex) {
          return catTypeIndex;
        }
      }
    }
  }
  return '';
}

export const getAssetCategoryAndTypeIndex = (application: Application) => {
  const assetCategoryIndex = getAssetCategoryIndex(application);
  const assetTypeIndex = getAssetTypeIndex(application);
  return `${assetCategoryIndex}-${assetTypeIndex}`;
}

export const getAssetSpec = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
    case 'Commercial':
      return application.AssetSpec;
  }
  return null;
}

export const getAssetMake = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.AssetSpec.make;
  }
  return '';
}

export const getAssetYear = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.AssetSpec.year;
  }
  return '';
}

export const getAssetFamily = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.AssetSpec.family;
  }
  return '';
}

export const getAssetPurposeDescription = (application: Application) => {
  return application.AppInfo.LoanPurpose?.purposeDescription ?? '';
}

export const getAssetExistingFinanceCommitment = (application: Application) => {
  return application.AppInfo.LoanPurpose?.hasExistingFinanceCommitments ?? false;
}

export const getAssetLVR = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.AssetSpec.LVR;
  }
  return '';
}
export const getAssetVehicle = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.AssetSpec.vehicle;
  }
  return '';
}
export const getAssetAvgRetail = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.AssetSpec.avgretail;
  }
  return 0;
}
export const getAssetGoodRetail = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.AssetSpec.goodretail;
  }
  return 0;
}
export const getAssetNewPrice = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.AssetSpec.newPrice;
  }
  return 0;
}
export const getAssetDescription = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.AssetSpec.description;
  }
  return '';
}

export const getSupplierName = (data: PpsrAsset) => {
  return data?.SettlementAssetDetails?.supplier ? (data?.SettlementAssetDetails?.supplier as AssetSupplier)?.SupplierName : `${data?.PrivateSellerDetails?.business ? data?.PrivateSellerDetails?.business.organisationName : data?.PrivateSellerDetails?.name + ' ' + data?.PrivateSellerDetails?.lastName}`
}

export const getAssetCondition = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.PricingDetails.AssetCondition;
  }
  return '';
}

export const getInvoiceAmount = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.PricingDetails.InvoiceAmount;
  }
  return 0;
}

export const getLoanTerms = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
    case 'InsurancePremium':
      return application.PricingDetails.LoanTerm;

  }
  return 0;
}

export const getRbaRate = (application: Application) => {
  switch (application.ApplicationType) {
    case 'BusinessOverdraft':
    case 'CorporateLoans':
      return application.PricingDetails.RbaRate;

  }
  return 0;
}

export const getPrivateSales = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.PricingDetails.PrivateSale;
  }
  return '';
}

export const getRateDiscount = (application: Application) => {
  switch(application.ApplicationType) {
    case 'Consumer':
      return application.PricingDetails.RateDiscount;
  }
  return 0;
}

export const getTransactionType = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      if (application.PricingDetails.TransactionType) {
        return application.PricingDetails.TransactionType;
      } else {
        if (application.PricingDetails.PrivateSale) {
          return 'Private Sale';
        } else {
          return 'Dealer Sale';
        }
      }

  }
  return '';
}

export const getEquifaxScoreAboveThresold = (application: Application) => {
  // Each product has its own Equifax Director Score threshold, matching the toggle label
  // shown on its pricing page. The stored EquifaxDirectorScoreThreshold string is the
  // single source of truth (historical rows were migrated by script 5.5.6-02).
  const derive = (required: '>=550' | '>=600' | '>=700'): 'Yes' | 'No' => {
    const pricing = application.PricingDetails as { EquifaxDirectorScoreThreshold?: string | null };
    return thresholdMeets(pricing?.EquifaxDirectorScoreThreshold, required) ? 'Yes' : 'No';
  };
  switch (application.ApplicationType) {
    case 'AssetFinance':
      return derive('>=550');
    case 'Consumer':
    case 'CorporateLoans':
      return derive('>=600');
    case 'BusinessLoans':
    case 'BusinessOverdraft':
      return derive('>=700');
  }
  return '';
}

export const getEquifaxDirectorScoreAbove700 = (application: Application): 'Yes' | 'No' => {
  const pricing = application?.PricingDetails as { EquifaxDirectorScoreThreshold?: string | null } | undefined;
  return thresholdMeets(pricing?.EquifaxDirectorScoreThreshold, '>=700') ? 'Yes' : 'No';
}

export const getLtv = (application: Application) => {
  switch(application.ApplicationType) {
    case 'CorporateLoans':
      return application.PricingDetails.ltv;
  }
  return undefined;
}

export const getSecurityType = (application: Application) => {
  switch(application.ApplicationType) {
    case 'CorporateLoans':
      return application.PricingDetails.securityType;
  }
  return undefined;
}

export const getAdverseOnFile = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
    case 'Consumer':
      return application.PricingDetails.AdverseOnFile;
  }
  return '';
}

export const getPropertyOwner = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
    case 'Consumer':
      return application.PricingDetails.PropertyOwner;
  }
  return '';
}

export const getHybrid = (application: Application) => {
  switch(application.ApplicationType) {
    case 'Consumer':
      return application.PricingDetails.Hybrid ?? "No";
  }
  return '';
}

export const getBrokerage = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
    case 'CorporateLoans':
      return Number(application.PricingDetails.Brokerage ? application.PricingDetails.Brokerage : 0);
  }
  return 0;
}

export const getBrokerageManualOverride = (application: Application): boolean => {
  if (application.ApplicationType === 'BusinessOverdraft') {
    return application.PricingDetails.BrokerageManualOverride === 'Yes';
  }
  return false;
}

export const getBrokerageAmount = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
    case 'Consumer':
    case 'InsurancePremium':
      return Number(application.PricingDetails.BrokerageAmount?application.PricingDetails.BrokerageAmount : 0 );
  }
  return 0;
}

export const getMonthlyAccountKeepingFee = (application: Application) => {
  switch(application.ApplicationType) {
    case 'Consumer':
      return Number(application.PricingDetails.MonthlyAccountKeepingFee ?? 0);
  }
  return 0;
}

export const getRepaymentPlusMonthlyAccountKeepingFee = (application: Application) => {
  switch(application.ApplicationType) {
    case 'Consumer':
      const paymentFrequency = getPaymentPeriod(application);
      const monthlyAccountKeepingFee = getMonthlyAccountKeepingFee(application);
      const repayment = Number(application?.PricingDetails?.Repayment ?? 0);
      if (paymentFrequency === 'Weekly') {
        return _.round(repayment + monthlyAccountKeepingFee / 4, 2);
      } else if (paymentFrequency === 'Fortnightly') {
        return _.round(repayment + monthlyAccountKeepingFee / 2, 2);
      } else {
        return _.round(repayment + monthlyAccountKeepingFee, 2);
      }
  }
  return 0;
}

export const getMargin = (application: Application) => {
  switch(application.ApplicationType) {
    case 'BusinessOverdraft':
    case 'CorporateLoans':
      return Number(application.PricingDetails.Margin ? application.PricingDetails.Margin : 0 );
  }
  return 0;
}

export const applicationToPaymentType = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessOverdraft':
    case 'Consumer':
      return application.PricingDetails.PaymentType;
    case 'BusinessLoans':
      return application.PricingDetails.PaymentType;
  }
  return null;
}

export const getCreditRateAdjustment = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
    case 'Consumer':
      return application.PricingDetails?.CreditRateAdjustment ?? 0;
  }
  return 0;
}

export const getOversCommission = (application: Application) => {
  switch(application.ApplicationType) {
    case 'Consumer':
      return application.PricingDetails?.OversCommission ?? 0;
  }
  return 0;
}

export const getFacilityEstablishmentFeePercent = (application: Application) => {
  switch(application.ApplicationType) {
    case 'CorporateLoans':
      return application.PricingDetails?.FacilityEstablishmentFeePercent;
  }
  return 0;
}

export const getFacilityEstablishmentFee = (application: Application) => {
  switch(application.ApplicationType) {
    case 'CorporateLoans':
      return application.PricingDetails?.FacilityEstablishmentFee;
  }
  return 0;
}

export const getPaymentType = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
    case 'Consumer':
      return application.PricingDetails.PaymentType ?? "Advance";
    case 'BusinessLoans':
      return application.PricingDetails.PaymentType ?? "Arrears";
  }
  return "";
}

export const getLVR = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer': {
      return application.AssetSpec.LVR ?? 0;
    }
  }
  return '';
}
export const getDirectorScoreRate = (application: Application) => {
  switch(application.ApplicationType) {
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'CorporateLoans':
      return application.PricingDetails.DirectorScoreRate ?? '';
  }
  return '';
}

export const getDocFeeFinanced = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
      return application.PricingDetails.DocFeeFinanced;
  }
  return '';
}

export const getBrokerOriginationFee = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
      return application.PricingDetails.BrokerOriginationFee;
  }
  return 0;
}

export const getBalloonPayment = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
      return application.PricingDetails.BalloonPayment;
  }
  return 0;
}

export const getRepaymentType = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'Consumer':
      return application.PricingDetails.RepaymentType;
  }
  return '';
}

export const getApr = (application: Application) => {
  switch(application.ApplicationType) {
    case 'Consumer':
      return application.PricingDetails.APR;
  }
  return 0;
}

export const getBalloonPaymentPercentage = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
      return application.PricingDetails.BalloonPaymentPercentage;
  }
  return 0;
}

export const getRepaymentFrequency = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
      return application.PricingDetails.PaymentPeriod;
  }
  return '';
}

export const getRepaymentFrequencyUnit = (application: Application): string => {
  let repaymentFrequency = null;
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
      repaymentFrequency = application.PricingDetails.PaymentPeriod;
  }
  switch(repaymentFrequency) {
    case 'Weekly':
      return 'Weeks';
    case 'Fortnightly':
      return 'Fortnight';
    case 'Monthly':
      return 'Months';
    default:
      return repaymentFrequency ?? '';
  }
}

export const getDeposit = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'Consumer':
      return application.PricingDetails.Deposit;
  }
  return '';
}

export const getLoanAmount = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
    case 'CorporateLoans':
      return application.PricingDetails.LoanAmount;
  }
  return 0;
}

export const getFacilityFee = (application: Application) => {
  switch(application.ApplicationType) {
    case 'CorporateLoans':
      return application.PricingDetails.FacilityEstablishmentFee;
  }
  return 0;
}

export const getFacilityFeePercent = (application: Application) => {
  switch(application.ApplicationType) {
    case 'CorporateLoans':
      return application.PricingDetails.FacilityEstablishmentFeePercent;
  }
  return 0;
}

export const getAdditionalCommission = (application: Application) => {
  if (application.ApplicationType === 'BusinessOverdraft') {
    return application.PricingDetails?.AdditionalCommission ?? 'No';
  }
  return '';
}

/**
 * Legacy Business Overdraft Facility Establishment Fee, surfaced in the UI
 * only for historical apps that were priced before BO moved to fixed
 * brokerage + Additional Commission. Returns 0 for non-BO apps and for
 * BO apps that don't have a non-zero legacy FEF (so callers can hide the cell).
 */
export const getBusinessOverdraftLegacyFacilityFee = (application: Application): number => {
  if (application.ApplicationType !== 'BusinessOverdraft') {
    return 0;
  }
  return application.PricingDetails?.FacilityEstablishmentFee ?? 0;
}

export const getBusinessOverdraftLegacyFacilityFeePercent = (application: Application): number => {
  if (application.ApplicationType !== 'BusinessOverdraft') {
    return 0;
  }
  return application.PricingDetails?.FacilityEstablishmentFeePercent ?? 0;
}

export const getRevenue = (application: Application) => {
  const commercialEntity = primaryEntityOf(application.parties);
  return commercialEntity?.revenue ?? '';
}

export const getLimitRequest = (application: Application) => {
  if (application.ApplicationType === 'TradeFinance' ||
    // application.ApplicationType === 'FloorplanFinance' ||
    application.ApplicationType === 'CorporateLoans') {
    return application.PricingDetails?.LimitRequest;
  }
  return '';
}

/**
 * The industry blocks are published as untyped JSON, so their `code`/`title` are read through this
 * rather than being reachable off the entity type.
 */
const industryDetail = (
  detail: JsonObject | null | undefined,
): { code?: string | null; title?: string | null } | null =>
  (detail ?? null) as { code?: string | null; title?: string | null } | null;

export const getApplicationPrimaryIndustry = (application: Application) => {
  const industry = industryDetail(primaryEntityOf(application.parties)?.industry?.industryDivision);
  if (industry) {
    return industry.title;
  }
  return '';
}

export const getIndustrySector = (application: Application) => {
  const industry = industryDetail(primaryEntityOf(application.parties)?.industry?.industryGroup);
  if (industry) {
    return industry.title;
  }
  return '';
}

export const getSubdivisionSector = (application: Application) => {
  const industry = industryDetail(primaryEntityOf(application.parties)?.industry?.industryClassDetail);
  if (industry) {
    return industry.title;
  }
  return '';
}

/**
 * Check if the application's industry is Transport (ANZSIC Division I)
 * Transport, Postal and Warehousing is Division I in ANZSIC
 */
export const isTransportIndustry = (application: Application): boolean => {
  const commercialEntity = primaryEntityOf(application.parties);
  if (!commercialEntity) return false;

  // Check IndustryDivision code directly (most reliable)
  if (industryDetail(commercialEntity.industry?.industryDivision)?.code === 'I') {
    return true;
  }

  return false;
}

export const getPrimaryBusinessAddress = (application: Application) => {
  const commercialEntity = primaryEntityOf(application.parties);
  return commercialEntity?.principalPlaceOfBusiness?.unformattedAddress ?? '';
}

export const getRegisteredBusinessAddress = (application: Application) => {
  const commercialEntity = primaryEntityOf(application.parties);
  return commercialEntity?.registeredBusinessAddress?.unformattedAddress ?? '';
}

export const getPrimaryBusinessAddressObject = (application: Application): Address2ComponentValue => {
  const commercialEntity = primaryEntityOf(application.parties);
  const address = commercialEntity?.principalPlaceOfBusiness ?? null;
  if (!address) {
    return null;
  } else {
    return {
      address: address?.unformattedAddress ?? "",
      StreetNumber: address?.streetNumber ?? "",
      StreetName: address?.streetName ?? "",
      StreetType: address?.streetType ?? "",
      Suburb: address?.suburb ?? "",
      State: address?.state ?? "",
      UnitNumber: address?.unitNumber ?? "",
      Postcode: address?.postcode ?? "",
    }
  }
}

export const getApplicationNotes = (application: Application) => {
  return application.ApplicationNotes ?? '';
}

export const getExpense = (application: Application) => {
  return {
    carLoans: application.Expense?.CarLoans ?? 0,
    creditCardLimit: application.Expense?.CreditCardLimit ?? 0,
    education: application.Expense?.Education ?? 0,
    electricity: application.Expense?.Electricity ?? 0,
    groceries: application.Expense?.Groceries ?? 0,
    homeLoans: application.Expense?.HomeLoans ?? 0,
    insurance: application.Expense?.Insurance ?? 0,
    otherLoans: application.Expense?.OtherLoans ?? 0,
    otherUtilities: application.Expense?.OtherUtilities ?? 0,
    personalLoans: application.Expense?.PersonalLoans ?? 0,
    shareOfExpense: application.Expense?.ShareofExpense ?? 0,
  }
}

export const getAdditionalSecurity = (application: Application) => {
  return application?.OppStatus?.Additional_Security__c ?? "";
}

export const getSpecialConditions = (application: Application) => {
  switch(application.ApplicationType) {
    case 'AssetFinance':
    case 'BusinessLoans':
    case 'BusinessOverdraft':
    case 'Consumer':
    case 'CorporateLoans':
      return application.PricingDetails?.SpecialConditions ?? "";
  }
  return "";
}

export const getVerimotoAssetGuid = (assetCategory: string, assetType: string): string | null => {
  switch (assetCategory) {
    case '137':
      return constants.verimotoAssets.find(obj => obj.name === 'Motor Vehicle')?.guid ?? null;
    case '132':
      return constants.verimotoAssets.find(obj => obj.name === 'Lorry/ Truck')?.guid ?? null;
    case '130':
      return constants.verimotoAssets.find(obj => obj.name === 'Plant and Equipment - Road')?.guid ?? null;
    case '140':
      return constants.verimotoAssets.find(obj => obj.name === 'Campervan/ Motorhome')?.guid ?? null;
    case '139':
      if (assetType === '1') {
        return constants.verimotoAssets.find(obj => obj.name === 'Motor Vehicle')?.guid ?? null;
      } else if (assetType === '2') {
        return constants.verimotoAssets.find(obj => obj.name === 'Campervan/ Motorhome')?.guid ?? null;
      } else if (assetType === '3') {
        return constants.verimotoAssets.find(obj => obj.name === 'Motor Vehicle')?.guid ?? null;
      } else {
        return null;
      }
    default:
      return null;
  }
}

export const getInspectionTypeGuid = (application: Application, assetGuid: string, verimotoAssetInspectionTypes: VerimotoInspectionTypeSelection[]): string | null => {
  const transactionType = getTransactionType(application);
  const assetGuidObject = constants.verimotoAssets.find(obj => obj.guid === assetGuid);
  const inspectionTypes = verimotoAssetInspectionTypes.filter(obj => obj.assetName === assetGuidObject?.name);
  if (transactionType === 'Private Sale') {
    return inspectionTypes.find(obj => obj.inspectionType === 'New Lending')?.guid ?? null;
  } else {
    return inspectionTypes.find(obj => obj.inspectionType === 'Personal Loan')?.guid ?? null;
  }
}

export const getTermRateFromBusinessOverdraftApplication = (
  application: BusinessOverdraftApplication,
  rateCard: RatecardDetails
): TermRateForBusinessOverdraft => {
  const pricing = application.PricingDetails;
  const loanAmount = pricing.LoanAmount ?? 0;
  const docFee = pricing.DocFee ?? 0;

  return {
    facilitylimit: loanAmount,
    docFee,
    brokerageDollar: pricing.BrokerageAmount ?? pricing.Brokerage ?? 0,
    brokerage: pricing.Brokerage ?? 0,
    interestRate: pricing.Rate ?? 0,
    rbaRate: pricing.RbaRate ?? rateCard.rbaRate ?? 0,
    margin: pricing.Margin ?? 0,
    monthlyFacilityFee: ((pricing.MonthlyFacilityFeePercentage ?? 0) / 100) * loanAmount / 12,
    monthlyFacilityFeePercentage: pricing.MonthlyFacilityFeePercentage ?? 0,
    earlyTerminationFee: pricing.EarlyTerminationFee ?? undefined,
    amountAvailable: loanAmount - docFee,
    calculationLog: [], // Empty calculation log for saved data
  };
};
/**
 * Portal display state for a privacy consent record.
 *
 * `styleKey` is a stable, CSS-class-friendly token (no spaces) for the status chip/icon; `label`
 * is the human text; `icon` is an mdi class for the consolidated per-individual status indicator.
 * Auto-generated consent forms (formType set) use the consent-form user story wording (Pending -
 * Customer Signature, Completed, Superseded, Expired, Dispatch Failed); legacy manual consent
 * records keep their raw created/opened/accepted status.
 */
/**
 * True when an 'expired' consent record is an aged *signature* (the customer signed but the signature
 * is now past CONSENT_MAX_AGE_DAYS and must be re-obtained), as opposed to a DocuSign envelope that
 * expired/voided before it was ever signed. Distinguished by signedTime: a signed-then-aged record
 * keeps its original signedTime, whereas an unsigned expiry never has one. Only meaningful when
 * status is 'expired'.
 */
export const isSignatureExpiry = (
  record: Pick<PrivacyConsent, 'status' | 'signedTime'>,
): boolean => {
  return record.status === 'expired' && !!record.signedTime;
};

export const privacyConsentDisplayStatus = (
  record: Pick<PrivacyConsent, 'status' | 'dispatchFailed' | 'formType' | 'signedTime' | 'bypassReason'>,
): { label: string; styleKey: string; icon: string } => {
  if (record.dispatchFailed) {
    return { label: CONSENT_DISPATCH_FAILED_SHORT_LABEL, styleKey: 'dispatchfailed', icon: 'mdi-alert-circle-outline' };
  }
  if (record.status === 'accepted') {
    // An admin bypass counts as complete everywhere, but must never read as a customer signature.
    if (record.bypassReason) {
      return { label: CONSENT_MANUALLY_COMPLETED_LABEL, styleKey: 'completed', icon: 'mdi-check-circle-outline' };
    }
    return record.formType
      ? { label: CONSENT_COMPLETED_LABEL, styleKey: 'completed', icon: 'mdi-check-circle-outline' }
      : { label: 'accepted', styleKey: 'accepted', icon: 'mdi-check-circle-outline' };
  }
  if (record.status === 'superseded') {
    return { label: CONSENT_SUPERSEDED_LABEL, styleKey: 'superseded', icon: 'mdi-history' };
  }
  // An unsigned form voided because a critical field changed; a fresh form has replaced it.
  if (record.status === 'invalidated') {
    return { label: CONSENT_INVALIDATED_LABEL, styleKey: 'invalidated', icon: 'mdi-link-off' };
  }
  if (record.status === 'expired') {
    // signedTime distinguishes an aged signature (must re-sign) from an envelope that expired unsigned.
    const label = record.signedTime ? CONSENT_EXPIRED_SIGNATURE_LABEL : CONSENT_EXPIRED_UNSIGNED_LABEL;
    return { label, styleKey: 'expired', icon: 'mdi-timer-off-outline' };
  }
  // Signer received the form and refused to sign. This is a terminal signer decision, not a dispatch
  // failure, so it does not trigger the "Action Required / resend contact details" broker banner.
  if (record.status === 'declined') {
    return { label: CONSENT_DECLINED_LABEL, styleKey: 'declined', icon: 'mdi-close-circle-outline' };
  }
  if (record.formType) {
    // 'created' and 'opened' both mean the form has gone out and is awaiting the customer's signature.
    return { label: CONSENT_PENDING_SIGNATURE_LABEL, styleKey: 'pending', icon: 'mdi-clock-outline' };
  }
  return { label: record.status, styleKey: record.status, icon: 'mdi-information-outline' };
};

/** True when any signatory's consent form failed to dispatch (application "Action Required" flag). */
export const hasConsentDispatchFailure = (records: Pick<PrivacyConsent, 'dispatchFailed'>[] | null | undefined): boolean => {
  return !!records?.some(record => record.dispatchFailed);
};

/**
 * The consent record that represents an individual's *current* consent state for the consolidated
 * view. Prefers the newest record that is not superseded or invalidated (accepted/expired/pending/
 * failed) by id, falling back to the newest record overall. Superseded/invalidated rows remain
 * available as history.
 */
export const currentPrivacyConsent = (records: PrivacyConsent[] | null | undefined): PrivacyConsent | null => {
  if (!records || !records.length) return null;
  const byIdDesc = [...records].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
  return byIdDesc.find(r => r.status !== 'superseded' && r.status !== 'invalidated') ?? byIdDesc[0];
};

/**
 * True when a signed consent's signature is older than the maximum permitted age and must be
 * treated as stale (so a credit bureau request rejects it and triggers a fresh consent).
 *
 * The comparison is calendar-day based and the boundary is inclusive: a signature exactly
 * `maxAgeDays` old is still valid; staleness begins the following calendar day (day 181 with the
 * default of 180). Days are compared at start-of-day so time-of-day never flips the result within a
 * single day. A missing `signedTime` (never signed) is not stale.
 */
export const isConsentSignatureStale = (
  signedTime: string | Date | null | undefined,
  asOf: Date = new Date(),
  maxAgeDays: number = CONSENT_MAX_AGE_DAYS,
): boolean => {
  if (!signedTime) return false;
  const signedMoment = moment(signedTime);
  if (!signedMoment.isValid()) return false;
  return moment(asOf).startOf('day').diff(signedMoment.startOf('day'), 'days') > maxAgeDays;
};

/**
 * True when any of the consent-critical prefill fields (name, DOB, address, email) differ between a
 * previously-dispatched form's prefill and a freshly-built one - i.e. the change requires the consent
 * form to be invalidated and reissued. Missing/absent values are treated as empty strings so a
 * previously-empty field being populated (or vice versa) counts as a change.
 */
export const consentCriticalFieldsChanged = (
  previous: Partial<ConsentFormPrefillData> | null | undefined,
  next: Partial<ConsentFormPrefillData> | null | undefined,
): boolean => {
  return CONSENT_FORM_CRITICAL_FIELDS.some(field => {
    const before = (previous?.[field] ?? '').toString().trim();
    const after = (next?.[field] ?? '').toString().trim();
    return before !== after;
  });
};

/**
 * True when a field that is sent to Equifax for the individual's credit file differs between two
 * consent prefills. Case and spacing are ignored because the Equifax identity key is normalised the
 * same way (normalizeEquifaxIdentityValue), so those edits cannot change the file.
 */
export const consentEquifaxFieldsChanged = (
  previous: Partial<ConsentFormPrefillData> | null | undefined,
  next: Partial<ConsentFormPrefillData> | null | undefined,
): boolean => {
  const normalise = (value: unknown) => (value ?? '').toString().trim().replace(/\s+/g, ' ').toUpperCase();
  return CONSENT_FORM_EQUIFAX_FIELDS.some(field => normalise(previous?.[field]) !== normalise(next?.[field]));
};

/**
 * Whether one individual's signed consents carry Equifax information the last credit flow run did
 * not see. The run is judged against the newest consent signed at or before `lastRunTime`: a
 * consent signed only after the run, or an earlier one that had expired (its bureau request was
 * blocked), counts as new, as does a field sent to Equifax differing between the two forms.
 * False when the individual has no signed consent at all (e.g. legacy checkbox consent), because
 * there is nothing to compare. Fails towards true when form data cannot be read.
 */
export const individualHasNewEquifaxInfoSinceRun = (
  signedConsents: Pick<PrivacyConsent, 'id' | 'status' | 'signedTime' | 'formData'>[],
  lastRunTime: Date,
): boolean => {
  const signed = signedConsents.filter(consent => !!consent.signedTime).sort((a, b) => b.id - a.id);
  const current = signed[0];
  if (!current) return false;
  const baseline = signed.find(consent => new Date(consent.signedTime as string).getTime() <= lastRunTime.getTime());
  if (!baseline) return true;
  if (baseline.id === current.id) return false;
  if (baseline.status === 'expired') return true;

  const previous = baseline.formData ? parseJSON(baseline.formData) : null;
  const next = current.formData ? parseJSON(current.formData) : null;
  if (typeof previous !== 'object' || !previous || typeof next !== 'object' || !next) return true;
  return consentEquifaxFieldsChanged(previous as ConsentFormPrefillData, next as ConsentFormPrefillData);
};

/**
 * True when a company-block prefill field (annual revenue, business phone, business email, principal
 * place of business) differs between two prefills. These print identically on every signatory's form,
 * so one change reissues all of them; compare only pre/post a save that edited the entity.
 */
export const consentCompanyBlockFieldsChanged = (
  previous: Partial<ConsentFormPrefillData> | null | undefined,
  next: Partial<ConsentFormPrefillData> | null | undefined,
): boolean => {
  return CONSENT_FORM_COMPANY_BLOCK_FIELDS.some(field => {
    const before = (previous?.[field] ?? '').toString().trim();
    const after = (next?.[field] ?? '').toString().trim();
    return before !== after;
  });
};

/** Timestamp of the latest status change for a consent record (Story 2 per-individual timestamp). */
export const latestConsentStatusChange = (
  record: Pick<PrivacyConsent, 'statusChangedTime' | 'lastUpdatedTime' | 'createdTime'>,
): string | null => {
  return record.statusChangedTime ?? record.lastUpdatedTime ?? record.createdTime ?? null;
};
